import { useEffect, useCallback, useRef } from 'react';
import { useDebounce } from 'usehooks-ts';

import { useFontDataHistory } from '../../../../hooks/editHistory/useFontDataHistory';
import { FontData, TextSource } from '../../../../types/text';

import { UseEditor } from './types';
import { getDataOnInput, getDataOnDelete } from './helpers';

export const useEditor: UseEditor = ({ data, setData, onCloseClick, analytics }) => {
  const firstUse = useRef(true);
  const cursorBeforeChange = useRef(0);
  const nextCursorPosition = useRef(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const addToHistory = useFontDataHistory((state) => state.add);

  const [text, , textS] = data;
  const debounceText = useDebounce(text, 1000);

  useEffect(() => {
    textS === TextSource.INTERNAL &&
      analytics.gaEvent('text_box_type', { page_name: window.location.pathname, action: 'editor' });
  }, [debounceText]);

  const onKeyDown = () => {
    cursorBeforeChange.current = textareaRef.current.selectionStart;
  };
  const onPaste = () => {
    cursorBeforeChange.current = textareaRef.current.selectionStart;
  };

  const onChange = async (e) => {
    const {
      nativeEvent: { inputType, data: input },
      target: { value: newText },
    } = e;
    let nextData: FontData;
    let newPosition: number;

    switch (inputType) {
      case 'deleteContentBackward':
      case 'deleteByCut':
      case 'deleteContentForward': {
        const { selectionStart } = textareaRef.current;

        nextData = getDataOnDelete(newText, data, selectionStart);
        nextCursorPosition.current = selectionStart;
        break;
      }
      case 'insertText': {
        [nextData, newPosition] = getDataOnInput(input, data, cursorBeforeChange.current);
        nextCursorPosition.current = newPosition;
        break;
      }
      case 'insertLineBreak': {
        [nextData, newPosition] = getDataOnInput('\n', data, cursorBeforeChange.current);
        nextCursorPosition.current = newPosition;
        break;
      }
      case 'insertFromPaste': {
        const pasted = await navigator.clipboard.readText();

        [nextData, newPosition] = getDataOnInput(pasted, data, cursorBeforeChange.current);
        nextCursorPosition.current = newPosition;
        break;
      }
      default: {
        return;
      }
    }
    setData(nextData);
  };

  const onClean = useCallback(() => {
    const { origin, pathname, search } = window.location;

    setData(['', [], TextSource.INTERNAL] as FontData);
    onCloseClick();
    search && history.pushState(null, '', origin + pathname);
    textareaRef.current.focus();
  }, [onCloseClick, textareaRef, setData]);

  useEffect(() => {
    if (firstUse.current) {
      firstUse.current = false;
      return;
    }

    const [, , textSource] = data;

    switch (textSource) {
      case TextSource.COPY: {
        addToHistory(data);

        if (textareaRef?.current) {
          const { current } = textareaRef;

          current.scrollTop = current.scrollHeight - current.clientHeight;
        }

        return;
      }
      case TextSource.HISTORY: {
        const pos = textareaRef?.current?.value.length;

        textareaRef?.current?.focus();
        textareaRef?.current?.setSelectionRange(pos, pos);
        return;
      }
      case TextSource.INTERNAL: {
        const pos = nextCursorPosition.current;

        addToHistory(data);
        textareaRef?.current?.focus();
        textareaRef?.current?.setSelectionRange(pos, pos);
        return;
      }
      default: {
        return;
      }
    }
  }, [data]);

  return [textareaRef, onPaste, onKeyDown, onChange, onClean];
};
